import type { SearchFieldProps } from "../../atoms/search-field/search-field";
import styles from "./channel-search.module.css";

/**
 * What both Channel Search twins share: the item contract, the ids, the copy
 * defaults and the one object of field chrome each twin spreads into its own
 * half of `atoms/search-field`.
 *
 * ⚠ The SHARED MARKUP IS THE FIELD AND NOTHING ELSE, and that is the design
 * rather than an omission. The suggestion list, the selection and the chips
 * have no zero-JavaScript form at all, so the server twin renders the pill and
 * stops; there is no server-rendered listbox to protect. Keeping the chrome in
 * one spread is what stops one twin growing an attribute the other forgets —
 * the same device `search-field.shared`'s own `searchFieldChrome` uses.
 *
 * ⛔ NO `"use client"` HERE, and nothing client-only may be imported into this
 * file: it is in the server twin's module graph, and a directive or a hook
 * would drag that twin across the boundary.
 */

/**
 * One row the reader can choose.
 *
 * ⛔ THE IDENTITY RULE. `id` is required, opaque and minted by the caller, and
 * NOTHING in this molecule may derive identity from `label` — not the dedupe,
 * not the selected state, not the toggle, not a React key. Three production
 * defects came from exactly that, each one level deeper than the last:
 * WAT-11767 keyed on the display text and collapsed two distinct channels into
 * one selection; WAT-11771 found the name + channel-number PAIR is not unique
 * either, because a channel number is a tuner POSITION two channels can share
 * and the live feed carries "Sports Mix" twice on 205; WAT-11775 landed on the
 * feed identity. The app's tiered fallback is domain knowledge and stays
 * app-side — see `src/components/video-lab/ChannelSearch/utils/choice-key.ts`.
 * What reaches the design system is the end of that story: one required `id`.
 *
 * Everything else is display-only. This molecule does not know what a channel
 * is: it renders `secondary`, `group`, `iconSrc` and `badge` and never reasons
 * about them.
 */
export interface ChannelSearchItem {
  /** ⛔ Required, opaque, unique. Never derived from `label`. */
  id: string;
  /** What is shown, and the only field the typed text is matched against. */
  label: string;
  /** Channel number, or any second line. Display only — NOT identity. */
  secondary?: string;
  /** Heading this row sits under, e.g. "Subscriptions". Falls back to `groupHeading`. */
  group?: string;
  /** Round logo. Decorative; `label` is the accessible name. */
  iconSrc?: string;
  /** Short qualifier beside the label, e.g. "Local", "RSN". */
  badge?: string;
}

/**
 * WHY the selection changed, reported beside the next list of ids.
 *
 * ⛔ THE CAUSE CANNOT BE RECOVERED FROM THE IDS, which is the whole reason this
 * exists. A chip removal and a re-chosen suggestion both hand the caller one
 * SHORTER list and the two are reported differently; a select-all whose query
 * matches exactly one row hands over one LONGER list, which is indistinguishable
 * from choosing that row and carries a different term. Diffing the two lists
 * tells none of them apart.
 *
 * ⚠ THIS SAYS WHAT HAPPENED AND NEVER WHAT TO REPORT. No wording, no placement,
 * no push: `secondary` is a second line here rather than a channel number (see
 * the item contract), so only the caller can name what the reader just did.
 */
export type ChannelSearchSelectionChange =
  /** SR6 — a suggestion the reader had not already chosen. */
  | { reason: "select"; item: ChannelSearchItem }
  /** SR6 — the same suggestion chosen again, which removes it. */
  | { reason: "deselect"; item: ChannelSearchItem }
  /** SR7 — a chip's own remove button. */
  | { reason: "remove-chip"; item: ChannelSearchItem }
  /**
   * SR5 — the select-all row, carrying the two halves the row was built from so
   * a caller can spell the term the row showed. Sent on EVERY activation,
   * including one that adds nothing, because the row was operated either way.
   * `prefix` is the RESOLVED copy, so a blank authored field reports the
   * default that is actually on screen.
   */
  | { reason: "select-all"; prefix: string; typedText: string }
  /** SR8 — Reset, or the ✕ where no Reset control is rendered. */
  | { reason: "reset" };

/** SR2 — below this, the list offers the typing hint instead of results. */
export const CHANNEL_SEARCH_MIN_QUERY_LENGTH = 2;

/**
 * The most suggestion rows drawn at once. A two-letter query matches well over
 * a hundred channels, each a row and an image; SR5's select-all still adds
 * every match, and the live region still counts them all.
 */
export const CHANNEL_SEARCH_MAX_SUGGESTIONS = 50;

/** The chip row's per-breakpoint limits. Every string the component draws is authored; none has a
 *  default here. */
export const CHANNEL_SEARCH_DEFAULTS = {
  chipRowLimitDesktop: 3,
  chipRowLimitMobile: 5
} as const;

/** An authored string, trimmed; absent reads as empty. */
export function channelSearchCopy(value: string | undefined): string {
  return value?.trim() ?? "";
}

/** Every content-managed string the component carries. */
export const CHANNEL_SEARCH_COPY_KEYS = [
  "label",
  "clearLabel",
  "ariaLabel",
  "typingHint",
  "selectAllPrefix",
  "groupHeading",
  "emptyMessage",
  "chipRemoveLabel",
  "showMoreLabel",
  "showLessLabel"
] as const;

export type ChannelSearchCopy = Record<
  (typeof CHANNEL_SEARCH_COPY_KEYS)[number],
  string
>;

/** Resolves the authored strings in one place. Keys it does not know are ignored, which is what
 *  lets the client twin collect them with a rest spread. */
export function resolveChannelSearchCopy(
  authored: Partial<ChannelSearchCopy>
): ChannelSearchCopy {
  const resolved = {} as ChannelSearchCopy;
  for (const key of CHANNEL_SEARCH_COPY_KEYS) {
    resolved[key] = channelSearchCopy(authored[key]);
  }
  return resolved;
}

/** Every id the component mints, derived from the one the caller gives it. */
export function channelSearchIds(id: string) {
  return {
    fieldId: `${id}-field`,
    listboxId: `${id}-listbox`,
    statusId: `${id}-status`,
    chipsId: `${id}-chips`
  };
}

export function channelSearchClasses(className?: string): string {
  return [styles.root, className].filter(Boolean).join(" ");
}

/**
 * The pill, configured identically on both twins. SR1's search icon is on, and
 * the ✕ is on so the term can be cleared before any JavaScript arrives.
 *
 * ⚠ There is NO `placeholder` here, and the contract's `placeholder` prop is
 * deliberately not published. `atoms/search-field` draws a floating label and
 * its `placeholder=" "` is load-bearing — a real placeholder string kills
 * `:placeholder-shown` and the prompt stops lifting. SR1's content-managed
 * string is therefore `label`. The Figma/implementation difference is reported
 * rather than papered over with a prop that would do nothing.
 */
export function channelSearchFieldProps({
  fieldId,
  label,
  clearLabel,
  ariaLabel,
  maxLength,
  disabled
}: {
  fieldId: string;
  label: string;
  clearLabel: string;
  ariaLabel: string;
  maxLength?: number;
  disabled?: boolean;
}): Pick<
  SearchFieldProps,
  "id" | "label" | "clearLabel" | "ariaLabel" | "maxLength" | "disabled" | "icon" | "clearable"
> {
  return {
    id: fieldId,
    label,
    clearLabel,
    ariaLabel,
    maxLength,
    disabled,
    icon: true,
    clearable: true
  };
}
